/**
 * The app's tooltip — **custom, because the native one is not fast enough to
 * read a table with.**
 *
 * `title` was doing this job everywhere: the caveat dots, the heatmap cells, the
 * em-dash columns. The browser holds it for roughly a second before showing
 * anything, hides it again after a few, and renders it in the OS font outside
 * our token system. On a grid where the *only* way to read a value is to hover
 * a cell, that delay makes the data feel unavailable.
 *
 * Kept deliberately small:
 * · **`position: fixed`**, measured from the target's own rect, so the bubble
 *   escapes the tables' `overflow-x: auto` instead of being clipped by it. No
 *   ancestor here creates a containing block for fixed elements (`sticky` does
 *   not; `transform` and `filter` would).
 * · **Clamped to the viewport**, and flipped below the target when there is no
 *   room above — a tooltip half off-screen is worse than the delay it replaced.
 * · **Hover *and* focus**, which is the reason `title` was used in the first
 *   place. The label stays on `aria-label`, so a screen reader is unaffected by
 *   any of this.
 * · No library, no portal, one element rendered at a time.
 */
import { useCallback, useRef, useState, type ReactNode } from 'react'

const BUBBLE = {
  position: 'fixed' as const,
  maxWidth: 'min(22rem, 90vw)',
  padding: '0.4rem 0.55rem',
  background: 'var(--bg-surface)',
  color: 'var(--text-primary)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-md)',
  boxShadow: 'var(--shadow-pop)',
  fontSize: '0.75rem',
  lineHeight: 1.45,
  // **X-10 / E-5 — the bubble is a DOM child of whatever it annotates.**
  // `position: fixed` escapes layout, not inheritance, so inside a
  // `<th className="uppercase">` the tooltip text arrived in caps with the
  // header's 0.04em tracking, while the same component on a KPI label read
  // normally. Dishant spotted exactly that pair: hovering "Author" (a column
  // header) shouted, hovering "Story Views" (a tile label) did not. These four
  // declarations end it for every tooltip in the app at once.
  textTransform: 'none' as const,
  letterSpacing: 'normal',
  fontWeight: 400,
  textAlign: 'left' as const,
  // Above the sticky sidebar, which creates its own stacking context.
  zIndex: 'var(--z-toast, 600)' as unknown as number,
  pointerEvents: 'none' as const,
  whiteSpace: 'normal' as const,
}

interface Placement {
  left: number
  top: number
  below: boolean
}

function place(rect: DOMRect): Placement {
  const margin = 8
  const below = rect.top < 120
  return {
    left: Math.min(Math.max(rect.left + rect.width / 2, margin + 120), window.innerWidth - margin - 120),
    top: below ? rect.bottom + margin : rect.top - margin,
    below,
  }
}

/** The bubble itself, positioned against a rect. Exported for the heatmap, which
 *  drives one shared bubble from delegated events over hundreds of cells rather
 *  than mounting a component per cell. */
export function HintBubble({ rect, children }: { rect: DOMRect; children: ReactNode }) {
  const at = place(rect)
  return (
    <div
      role="tooltip"
      style={{
        ...BUBBLE,
        left: at.left,
        top: at.top,
        transform: at.below ? 'translate(-50%, 0)' : 'translate(-50%, -100%)',
      }}
    >
      {children}
    </div>
  )
}

/**
 * Wraps one element and shows `label` beside it on hover or focus.
 *
 * `as="span"` by default; the caveat dot needs to sit inline inside a table
 * header, so the wrapper must not introduce a block.
 */
export function Hint({
  label,
  children,
  className,
  style,
  tabIndex = 0,
}: {
  label: ReactNode
  children: ReactNode
  className?: string
  style?: React.CSSProperties
  tabIndex?: number
}) {
  const ref = useRef<HTMLSpanElement>(null)
  const [rect, setRect] = useState<DOMRect | null>(null)

  const show = useCallback(() => {
    const element = ref.current
    if (element) setRect(element.getBoundingClientRect())
  }, [])
  const hide = useCallback(() => setRect(null), [])

  return (
    <span
      ref={ref}
      className={className}
      style={style}
      tabIndex={tabIndex}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
      // Escape closes it, so a keyboard reader is never stuck with a bubble
      // covering the cell they are trying to read.
      onKeyDown={(event) => {
        if (event.key === 'Escape') hide()
      }}
    >
      {children}
      {rect && <HintBubble rect={rect}>{label}</HintBubble>}
    </span>
  )
}
